import gql from 'graphql-tag';

export const PROJECTS_FRAGMENT = gql`
  fragment Projects on Project {
    id
    name
    description
    uploadStatus
    creditsOutstanding
    creditsWaived
    creditBreakdown {
      pointCount
      required
      singleProjectMaxPoints
    }
    isInitializing
    isLocal
    storageBaseUrl
    storageContainer
    copy {
      type
      sourceProjectId
    }
    assignee {
      id
      name
    }
    pointClouds {
      id
      pointCount
      cloudName
      validation {
        status
        isValid
        finishedAt
      }
    }
    previewUrl
  }
`;

export const PROJECT_STATE_FRAGMENT = gql`
  fragment ProjectState on ProjectState {
    annotations {
      identifier
      groupIdentifier
      type
    }
    groups {
      type
      identifier
      name
      parentIdentifier
    }
    orderedIdentifiers {
      identifier
      index
    }
    wmsLayerOrderedIdentifiers {
      identifier
      index
    }
    orthophotoLayerOrderedIdentifiers {
      identifier
      index
    }
    cadLayers {
      identifier
      name
      fileUrl
      fileName
      visibleState {
        ... on CadLayerVisibleStateDelete {
          index
          isGroup
          subIndex
        }
        ... on CadLayerVisibleStateHidden {
          index
          isGroup
          isLayer
          layerIdentifier
          subIndex
          visible
          userId
        }
      }
    }
    wmsLayers {
      identifier
      type
      name
      url
      username
      password
      layerName
      visible
    }
    orthophotoLayers {
      identifier
      name
      visible
      url
      elevation
    }
  }
`;

export const PROJECT_CALCULATION_FRAGMENT = gql`
  fragment Calculation on Calculation {
    id
    status
    annotationIdentifier
    pointCloudIdentifier
    ... on VolumeCalculation {
      baseSurface
      result {
        volume
        area
        volumePositive
        volumeNegative
      }
      paramInfo {
        referenceHeight
        heightFilters
        intensityFilters
        classificationFilters
        resolutionFilter
        clippingFilter
      }
      isOutDated
    }
    ... on ProjectionCalculation {
      profileGroupIdentifier
      result {
        projection {
          distance
          height
        }
      }
      paramInfo {
        heightFilters
        intensityFilters
        classificationFilters
        resolutionFilter
        clippingFilter
      }
      isOutDated
    }
  }
`;

export const PROJECT_EXPORT_FRAGMENT = gql`
  fragment Export on Export {
    exportId
    status
    type
    createdAt
    fileSize
    seen
    paramInfo {
      ... on PointCloudExportParamInfo {
        fileType
        heightFilters
        classificationFilters
        resolutionFilter
        clippingFilter
      }
    }
  }
`;

/**
 * Fragment for non-annotation project fields. Used with Apollo's useFragment
 * to subscribe to project metadata without re-rendering when annotations change.
 */
export const PROJECT_SHELL_FRAGMENT = gql`
  ${PROJECT_EXPORT_FRAGMENT}
  fragment ProjectShell on Project {
    id
    name
    description
    uploadStatus
    isLocal
    storageBaseUrl
    storageContainer
    copy {
      type
    }
    myAccessLevel
    assignee {
      id
      name
    }
    pointClouds {
      id
      hasClassifications
      hasSources
      uploadStatus
      cloudName
      availableClasses
      availableSources
      visible
      displayName
      pointCount
    }
    state {
      wmsLayerOrderedIdentifiers {
        identifier
        index
      }
      orthophotoLayerOrderedIdentifiers {
        identifier
        index
      }
      cadLayers {
        identifier
        name
        fileUrl
        fileName
        visibleState {
          ... on CadLayerVisibleStateDelete {
            index
            isGroup
            subIndex
          }
          ... on CadLayerVisibleStateHidden {
            index
            isGroup
            isLayer
            layerIdentifier
            subIndex
            visible
            userId
          }
        }
      }
      wmsLayers {
        identifier
        type
        name
        url
        username
        password
        layerName
        visible
      }
      orthophotoLayers {
        identifier
        name
        visible
        url
        elevation
      }
    }
    settings {
      projectionSystem
    }
    exports {
      ...Export
    }
    mapVisible
    comments {
      id
      comment
      user {
        id
        name
      }
      referenceIdentifier
      createdAt
      updatedAt
    }
    customClasses {
      id
      name
      color {
        r
        g
        b
        a
      }
      code
    }
    previewUrl
  }
`;

export const PROJECT_FRAGMENT = gql`
  ${PROJECT_STATE_FRAGMENT}
  ${PROJECT_CALCULATION_FRAGMENT}
  ${PROJECT_EXPORT_FRAGMENT}
  fragment Project on Project {
    id
    name
    description
    uploadStatus
    isLocal
    storageBaseUrl
    storageContainer
    copy {
      type
    }
    myAccessLevel
    assignee {
      id
      name
    }
    pointClouds {
      id
      hasClassifications
      hasSources
      uploadStatus
      cloudName
      availableClasses
      availableSources
      visible
      displayName
      pointCount
    }
    state {
      ...ProjectState
    }
    settings {
      projectionSystem
    }
    calculations {
      ...Calculation
    }
    exports {
      ...Export
    }
    mapVisible
    comments {
      id
      comment
      user {
        id
        name
      }
      referenceIdentifier
      createdAt
      updatedAt
    }
    customClasses {
      id
      name
      color {
        r
        g
        b
        a
      }
      code
    }
    previewUrl
  }
`;

export const PROJECTS = gql`
  ${PROJECTS_FRAGMENT}
  query projects($organisationId: ID!) {
    projectsByOrganisationId(organisationId: $organisationId) {
      ...Projects
    }
  }
`;

export const PROJECT = gql`
  ${PROJECT_FRAGMENT}
  query projectById($projectId: ID!) {
    projectById(projectId: $projectId) {
      ...Project
    }
  }
`;

/**
 * Focused query for annotation state + calculations.
 * Shares the same Apollo cache entries as PROJECT but only subscribes
 * to annotation-related fields, so changes to other project fields
 * (settings, pointClouds, comments, etc.) won't trigger re-renders
 * in annotation consumers.
 */
export const PROJECT_ANNOTATION_STATE = gql`
  ${PROJECT_CALCULATION_FRAGMENT}
  query projectAnnotationState($projectId: ID!) {
    projectById(projectId: $projectId) {
      id
      state {
        annotations {
          identifier
          groupIdentifier
          type
        }
        groups { type identifier name parentIdentifier }
        orderedIdentifiers { identifier index }
      }
      calculations {
        ...Calculation
      }
    }
  }
`;

export const PROJECT_CALCULATIONS = gql`
  ${PROJECT_CALCULATION_FRAGMENT}
  query projectCalculations($projectId: ID!) {
    projectById(projectId: $projectId) {
      id
      calculations {
        ...Calculation
      }
    }
  }
`;

export const PROJECT_EXPORTS = gql`
  ${PROJECT_EXPORT_FRAGMENT}
  query projectExports($projectId: ID!) {
    projectById(projectId: $projectId) {
      id
      exports {
        ...Export
      }
    }
  }
`;

export const PROJECT_DELETE_EXPORT = gql`
  ${PROJECT_EXPORT_FRAGMENT}
  mutation projectDeleteExport($projectId: ID!, $exportId: String!) {
    projectDeleteExport(projectId: $projectId, exportId: $exportId) {
      id
      exports {
        ...Export
      }
    }
  }
`;

export const PROJECT_MARK_EXPORTS_SEEN = gql`
  ${PROJECT_EXPORT_FRAGMENT}
  mutation projectMarkExportsSeen($projectId: ID!, $exportIds: [String!]!) {
    projectMarkExportsSeen(projectId: $projectId, exportIds: $exportIds) {
      id
      exports {
        ...Export
      }
    }
  }
`;

export const REQUEST_UPLOAD_SAS = gql`
  query projectRequestUploadSAS($projectId: ID!) {
    projectRequestUploadSAS(projectId: $projectId)
  }
`;

export const PROJECT_SHARE_FRAGMENT = gql`
  fragment ProjectShare on Project {
    id
    share {
      enabled
      token
      createdAt
    }
  }
`;

export const PROJECT_SHARE = gql`
  ${PROJECT_SHARE_FRAGMENT}
  query projectShare($projectId: ID!) {
    projectById(projectId: $projectId) {
      ...ProjectShare
    }
  }
`;

export const PROJECT_SHARE_SET = gql`
  ${PROJECT_SHARE_FRAGMENT}
  mutation projectShareSet($projectId: ID!, $enabled: Boolean!) {
    projectShareSet(projectId: $projectId, enabled: $enabled) {
      ...ProjectShare
    }
  }
`;

export const PROJECT_SHARE_RESET = gql`
  ${PROJECT_SHARE_FRAGMENT}
  mutation projectShareReset($projectId: ID!) {
    projectShareReset(projectId: $projectId) {
      ...ProjectShare
    }
  }
`;

/** Which project the share token in the request header unlocks (null: no valid link). */
export const SHARED_PROJECT_INFO = gql`
  query sharedProjectInfo {
    sharedProjectInfo {
      projectId
      organisationId
      name
    }
  }
`;

export const REQUEST_READ_SAS_TOKEN = gql`
  query projectRequestReadSASToken($projectId: ID!) {
    projectRequestReadSASToken(projectId: $projectId)
  }
`;

export const PROJECT_START = gql`
  ${PROJECTS_FRAGMENT}
  mutation projectStart($projectId: ID!, $fileNames: [String!]!, $cloudNames: [String!], $organisationId: ID!, $name: String!, $description: String!, $shouldMerge: Boolean) {
    projectStart(projectId: $projectId, fileNames: $fileNames, cloudNames: $cloudNames, organisationId: $organisationId, name: $name, description: $description, shouldMerge: $shouldMerge) {
      ...Projects
    }
  }
`;

export const PROJECT_RELAUNCH = gql`
  ${PROJECTS_FRAGMENT}
  mutation projectRelaunch($projectId: ID!) {
    projectRelaunch(projectId: $projectId) {
      ...Projects
    }
  }
`;

export const PROJECT_VALIDATE_POINTCLOUD = gql`
  ${PROJECTS_FRAGMENT}
  mutation projectValidatePointCloud($projectId: ID!, $cloudName: String!) {
    projectValidatePointCloud(projectId: $projectId, cloudName: $cloudName) {
      ...Projects
    }
  }
`;

export const PROJECT_START_WITH_PYTHAGORAS_FILE = gql`
  ${PROJECTS_FRAGMENT}
  mutation projectStartWithPythagorasFiles($projectId: ID!, $cloudName: String!, $organisationId: ID!, $name: String!, $description: String!, $pointCount: BigInt) {
    projectStartWithPythagorasFiles(projectId: $projectId, cloudName: $cloudName, organisationId: $organisationId, name: $name, description: $description, pointCount: $pointCount) {
      ...Projects
    }
  }
`;

export const PROJECT_UPDATE = gql`
  ${PROJECTS_FRAGMENT}
  mutation projectUpdate($projectId: ID!, $name: String!, $description: String!) {
    projectUpdate(projectId: $projectId, name: $name, description: $description) {
      ...Projects
    }
  }
`;

export const PROJECT_UPDATE_PREVIEW = gql`
  ${PROJECTS_FRAGMENT}
  mutation projectUpdatePreviewImage($projectId: ID!, $previewImageFileName: String!) {
    projectUpdatePreviewImage(projectId: $projectId, previewImageFileName: $previewImageFileName) {
      ...Projects
    }
  }
`;

export const PROJECT_UPDATE_SETTINGS = gql`
  mutation projectUpdateSettings($projectId: ID!, $settings: InputProjectSettings!) {
    projectUpdateSettings(projectId: $projectId, settings: $settings) {
      id
      settings {
        projectionSystem
      }
    }
  }
`;

export const PROJECT_DELETE = gql`
  mutation projectDelete($projectId: ID!) {
    projectDelete(projectId: $projectId)
  }
`;

export const PROJECT_ACTIVATE = gql`
  mutation projectActivate($projectId: ID!) {
    projectActivate(projectId: $projectId) {
      uploadStatus
    }
  }
`;

export const SUPERADMIN_UNLOCK_PROJECT = gql`
  mutation superadminUnlockProject($projectId: ID!) {
    superadminUnlockProject(projectId: $projectId) {
      id
      uploadStatus
      creditsOutstanding
      creditsWaived
    }
  }
`;

export const SUPERADMIN_DEBUG_SET_PROJECT_STATE = gql`
  mutation superadminDebugSetProjectState($projectId: ID!, $state: InputProjectDebugState!) {
    superadminDebugSetProjectState(projectId: $projectId, state: $state) {
      id
      uploadStatus
      creditsOutstanding
      creditsWaived
      creditBreakdown {
        pointCount
        required
        singleProjectMaxPoints
      }
      pointClouds {
        id
        pointCount
      }
    }
  }
`;

export const PROJECT_CHARGE_CREDITS = gql`
  mutation projectChargeCredits($projectId: ID!) {
    projectChargeCredits(projectId: $projectId) {
      id
      uploadStatus
      creditsOutstanding
    }
  }
`;

export const PROJECT_DUPLICATE = gql`
  mutation projectDuplicate($projectId: ID!, $name: String!, $description: String!) {
    projectDuplicate(projectId: $projectId, name: $name, description: $description) {
      id
    }
  }
`;

export const PROJECT_COPY_TO_ORGANISATION = gql`
  mutation projectCopyToOrganisation($projectId: ID!, $name: String!, $description: String, $targetOrganisationId: ID!) {
    projectCopyToOrganisation(projectId: $projectId, name: $name, description: $description, targetOrganisationId: $targetOrganisationId) {
      id
    }
  }
`;

export const PROJECT_CLONE_FROM_CLOUD = gql`
  mutation projectCloneFromCloud($projectId: ID!, $name: String!, $targetOrganisationId: ID!) {
    projectCloneFromCloud(projectId: $projectId, name: $name, targetOrganisationId: $targetOrganisationId) {
      id
    }
  }
`;

export const IS_LOCAL_STORAGE_ENABLED = gql`
  query isLocalStorageEnabled {
    isLocalStorageEnabled
  }
`;

export const PROJECT_CONFIRM_UPLOAD = gql`
  mutation projectConfirmPointCloudUpload($projectId: ID!, $fileNames: [String!]!, $shouldMerge: Boolean) {
    projectConfirmPointCloudUpload(projectId: $projectId, fileNames: $fileNames, shouldMerge: $shouldMerge)
  }
`;

export const PROJECT_CREATE_ANNOTATION = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectCreateAnnotation($projectId: ID!, $identifier: String!, $type: AnnotationType!, $groupIdentifier: String, $initialProperties: [PropertyUpdateInput!], $clientId: String) {
    projectCreateAnnotation(projectId: $projectId, identifier: $identifier, type: $type, groupIdentifier: $groupIdentifier, initialProperties: $initialProperties, clientId: $clientId) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_CREATE_GROUP = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectCreateGroup($projectId: ID!, $identifier: String!, $name: String!, $type: GroupType, $annotationIdentifiers: [String!]!, $parentIdentifier: String) {
    projectCreateGroup(projectId: $projectId, identifier: $identifier, name: $name, type: $type, annotationIdentifiers: $annotationIdentifiers, parentIdentifier: $parentIdentifier) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_DELETE_SELECTED = gql`
  mutation projectDeleteSelected($projectId: ID!, $annotationIdentifiers: [String!]!, $groupIdentifiers: [String!]!, $clientId: String) {
    projectDeleteSelected(projectId: $projectId, annotationIdentifiers: $annotationIdentifiers, groupIdentifiers: $groupIdentifiers, clientId: $clientId) {
      id
    }
  }
`;

export const PROJECT_MOVE_ANNOTATIONS_TO_GROUP = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectMoveAnnotationsToGroup($projectId: ID!, $annotationIdentifiers: [String!]!, $groupIdentifier: String) {
    projectMoveAnnotationsToGroup(projectId: $projectId, annotationIdentifiers: $annotationIdentifiers, groupIdentifier: $groupIdentifier) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_UNGROUP = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectUngroup($projectId: ID!, $identifier: String!) {
    projectUngroup(projectId: $projectId, identifier: $identifier) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_UPDATE_ORDERING = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectUpdateOrdering($projectId: ID!, $identifiers: [UpdateOrderingIdentifier!]!, $toIndex: Int!, $toGroupIdentifier: String) {
    projectUpdateOrdering(projectId: $projectId, identifiers: $identifiers, toIndex: $toIndex, toGroupIdentifier: $toGroupIdentifier) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

// --- Layer mutations ---

export const PROJECT_CREATE_CAD_LAYER = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectCreateCadLayer($projectId: ID!, $cadLayerId: String!, $name: String!, $fileName: String!) {
    projectCreateCadLayer(projectId: $projectId, cadLayerId: $cadLayerId, name: $name, fileName: $fileName) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_DELETE_CAD_LAYER = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectDeleteCadLayer($projectId: ID!, $cadLayerIdentifier: String!) {
    projectDeleteCadLayer(projectId: $projectId, cadLayerIdentifier: $cadLayerIdentifier) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_DELETE_CAD_OBJECT = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectDeleteCadObject($projectId: ID!, $cadLayerIdentifier: String!, $index: Int!, $subIndex: Int) {
    projectDeleteCadObject(projectId: $projectId, cadLayerIdentifier: $cadLayerIdentifier, index: $index, subIndex: $subIndex) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_DELETE_CAD_OBJECT_GROUP = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectDeleteCadObjectGroup($projectId: ID!, $cadLayerIdentifier: String!, $index: Int!) {
    projectDeleteCadObjectGroup(projectId: $projectId, cadLayerIdentifier: $cadLayerIdentifier, index: $index) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_HIDE_CAD_OBJECT = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectHideCadObject($projectId: ID!, $cadLayerIdentifier: String!, $index: Int!, $subIndex: Int, $isGroup: Boolean, $isLayer: Boolean, $visible: Boolean!) {
    projectHideCadObject(projectId: $projectId, cadLayerIdentifier: $cadLayerIdentifier, index: $index, subIndex: $subIndex, isGroup: $isGroup, isLayer: $isLayer, visible: $visible) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_CONVERT_CAD_OBJECT = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectConvertCadObject($projectId: ID!, $cadObjects: [InputCadObject!]!) {
    projectConvertCadObject(projectId: $projectId, cadObjects: $cadObjects) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_CREATE_WMS_LAYER = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectCreateWmsLayer($projectId: ID!, $layerId: String!, $name: String!, $url: String!, $username: String, $password: String, $layerName: String!, $type: WMSType!) {
    projectCreateWmsLayer(projectId: $projectId, layerId: $layerId, name: $name, url: $url, username: $username, password: $password, layerName: $layerName, type: $type) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_DELETE_WMS_LAYER = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectDeleteWmsLayer($projectId: ID!, $wmsLayerIdentifier: String!) {
    projectDeleteWmsLayer(projectId: $projectId, wmsLayerIdentifier: $wmsLayerIdentifier) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_UPDATE_WMS_LAYER = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectUpdateWmsLayer($projectId: ID!, $layerId: String!, $name: String!, $url: String!, $username: String, $password: String, $layerName: String!, $type: WMSType!) {
    projectUpdateWmsLayer(projectId: $projectId, layerId: $layerId, name: $name, url: $url, username: $username, password: $password, layerName: $layerName, type: $type) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_UPDATE_WMS_LAYER_ORDERING = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectUpdateWmsLayerOrdering($projectId: ID!, $identifiers: [String!]!, $toIndex: Int!) {
    projectUpdateWmsLayerOrdering(projectId: $projectId, identifiers: $identifiers, toIndex: $toIndex) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_CREATE_ORTHOPHOTO_LAYER = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectCreateOrthophotoLayer($projectId: ID!, $layerId: String!, $name: String!) {
    projectCreateOrthophotoLayer(projectId: $projectId, layerId: $layerId, name: $name) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_DELETE_ORTHOPHOTO_LAYER = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectDeleteOrthophotoLayer($projectId: ID!, $orthophotoLayerIdentifier: String!) {
    projectDeleteOrthophotoLayer(projectId: $projectId, orthophotoLayerIdentifier: $orthophotoLayerIdentifier) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_UPDATE_ORTHOPHOTO_LAYER_ORDERING = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectUpdateOrthophotoLayerOrdering($projectId: ID!, $identifiers: [String!]!, $toIndex: Int!) {
    projectUpdateOrthophotoLayerOrdering(projectId: $projectId, identifiers: $identifiers, toIndex: $toIndex) {
      id
      state {
        ...ProjectState
      }
    }
  }
`;

export const PROJECT_UPDATE_ENTITY_NAME = gql`
  ${PROJECT_STATE_FRAGMENT}
  mutation projectUpdateEntityName($projectId: ID!, $name: String!, $groupIdentifier: String, $pointCloudIdentifier: String, $cadLayerIdentifier: String, $wmsLayerIdentifier: String, $orthophotoLayerIdentifier: String) {
    projectUpdateEntityName(
      projectId: $projectId
      name: $name
      groupIdentifier: $groupIdentifier
      pointCloudIdentifier: $pointCloudIdentifier
      cadLayerIdentifier: $cadLayerIdentifier
      wmsLayerIdentifier: $wmsLayerIdentifier
      orthophotoLayerIdentifier: $orthophotoLayerIdentifier
    ) {
      id
      state {
        ...ProjectState
      }
      pointClouds {
        id
        displayName
      }
    }
  }
`;

export const PROJECT_TOGGLE_OBJECT_VISIBILITY = gql`
  mutation projectToggleObjectVisibility($projectId: ID!, $identifiers: [String!]!, $visible: Boolean!) {
    projectToggleObjectVisibility(projectId: $projectId, identifiers: $identifiers, visible: $visible) {
      id
      state {
        annotations {
          identifier
        }
        wmsLayers {
          identifier
          visible
        }
        orthophotoLayers {
          identifier
          visible
        }
      }
      pointClouds {
        id
        visible
      }
      mapVisible
    }
  }
`;

export const PROJECT_VOLUME_CALCULATE = gql`
  ${PROJECT_CALCULATION_FRAGMENT}
  mutation projectVolumeCalculate(
    $projectId: ID!
    $pointCloudId: ID!
    $annotationIdentifier: String!
    $referenceHeight: Float
    $baseSurface: CalculationBaseSurfaces!
    $classificationFilters: [Int!]
    $heightFilters: [Int]
    $intensityFilters: [Int]
    $resolutionFilter: Int
    $clippingFilter: Int
  ) {
    projectVolumeCalculate(
      projectId: $projectId
      pointCloudId: $pointCloudId
      annotationIdentifier: $annotationIdentifier
      referenceHeight: $referenceHeight
      baseSurface: $baseSurface
      classificationFilters: $classificationFilters
      heightFilters: $heightFilters
      intensityFilters: $intensityFilters
      resolutionFilter: $resolutionFilter
      clippingFilter: $clippingFilter
    ) {
      id
      calculations {
        ...Calculation
      }
    }
  }
`;

export const PROJECT_EXPORT_POINTCLOUD = gql`
  ${PROJECT_EXPORT_FRAGMENT}
  mutation projectExportPointcloud($projectId: ID!, $pointCloudId: ID!, $fileType: FileTypes, $heightFilters: [Int], $intensityFilters: [Int], $classificationFilters: [Int], $resolutionFilter: Int, $clippingFilter: Int) {
    projectExportPointcloud(
      projectId: $projectId
      pointCloudId: $pointCloudId
      fileType: $fileType
      heightFilters: $heightFilters
      intensityFilters: $intensityFilters
      classificationFilters: $classificationFilters
      resolutionFilter: $resolutionFilter
      clippingFilter: $clippingFilter
    ) {
      id
      exports {
        ...Export
      }
    }
  }
`;

export const PROJECT_PROJECTION_CALCULATE = gql`
  ${PROJECT_CALCULATION_FRAGMENT}
  mutation projectProjectionCalculate(
    $projectId: ID!
    $pointCloudId: ID!
    $annotationIdentifier: String!
    $profileGroupIdentifier: String!
    $classificationFilters: [Int!]
    $heightFilters: [Int]
    $intensityFilters: [Int]
    $resolutionFilter: Int
    $clippingFilter: Int
  ) {
    projectProjectionCalculate(
      projectId: $projectId
      pointCloudId: $pointCloudId
      annotationIdentifier: $annotationIdentifier
      profileGroupIdentifier: $profileGroupIdentifier
      classificationFilters: $classificationFilters
      heightFilters: $heightFilters
      intensityFilters: $intensityFilters
      resolutionFilter: $resolutionFilter
      clippingFilter: $clippingFilter
    ) {
      id
      calculations {
        ...Calculation
      }
    }
  }
`;

export const PROJECT_DELETE_CALCULATION = gql`
  ${PROJECT_CALCULATION_FRAGMENT}
  mutation projectDeleteCalculation($projectId: ID!, $calculationId: ID!) {
    projectDeleteCalculation(projectId: $projectId, calculationId: $calculationId) {
      id
      calculations {
        ...Calculation
      }
    }
  }
`;

export const PROJECT_GENERATE_CLASSIFICATIONS = gql`
  ${PROJECT_CALCULATION_FRAGMENT}
  mutation projectGenerateClassifications($projectId: ID!, $cloudName: String!) {
    projectGenerateClassifications(projectId: $projectId, cloudName: $cloudName) {
      id
      pointClouds {
        hasClassifications
      }
    }
  }
`;

export const POINT_CLOUD_DELETE = gql`
  mutation projectPointCloudDelete($projectId: ID!, $pointCloudId: ID!) {
    projectPointCloudDelete(projectId: $projectId, pointCloudId: $pointCloudId) {
      id
      pointClouds {
        id
        hasClassifications
        hasSources
        uploadStatus
        cloudName
        availableClasses
        availableSources
        visible
        displayName
        pointCount
      }
      state {
        annotations {
          identifier
        }
      }
    }
  }
`;

export const POINTCLOUD_UPLOAD_STATUS = gql`
  ${PROJECT_FRAGMENT}
  query pointCloudUploadStatus($projectId: ID!) {
    projectById(projectId: $projectId) {
      id
      pointClouds {
        id
        uploadStatus
      }
    }
  }
`;

export const PROJECT_INITIALIZE = gql`
  mutation projectInitialize($organisationId: ID!) {
    projectInitialize(organisationId: $organisationId) {
      id
    }
  }
`;

export const PROJECT_ADD_COMMENT = gql`
  mutation projectAddComment($projectId: ID!, $comment: String!, $referenceIdentifier: String!) {
    projectAddComment(projectId: $projectId, comment: $comment, referenceIdentifier: $referenceIdentifier) {
      comments {
        id
      }
    }
  }
`;

export const PROJECT_DELETE_COMMENT = gql`
  mutation projectDeleteComment($commentId: ID!, $projectId: ID!) {
    projectDeleteComment(commentId: $commentId, projectId: $projectId) {
      id
      comments {
        id
      }
    }
  }
`;

export const PROJECT_EDIT_COMMENT = gql`
  mutation projectEditComment($commentId: ID!, $projectId: ID!, $newComment: String!) {
    projectEditComment(commentId: $commentId, projectId: $projectId, updatedComment: $newComment) {
      id
      comments {
        id
        __typename
        comment
      }
    }
  }
`;

export const PROJECT_ADD_CUSTOM_CLASS = gql`
  mutation projectAddCustomClass($projectId: ID!, $name: String!, $color: InputColor!) {
    projectAddCustomClass(projectId: $projectId, name: $name, color: $color) {
      id
      customClasses {
        id
        name
        color {
          r
          g
          b
          a
        }
        code
      }
    }
  }
`;

export const PROJECT_DELETE_CUSTOM_CLASS = gql`
  mutation projectDeleteCustomClass($customClassId: ID!, $projectId: ID!) {
    projectDeleteCustomClass(customClassId: $customClassId, projectId: $projectId) {
      id
      customClasses {
        id
      }
    }
  }
`;

export const PROJECT_EDIT_CUSTOM_CLASS_NAME = gql`
  mutation projectEditCustomClassName($customClassId: ID!, $projectId: ID!, $newName: String!) {
    projectEditCustomClassName(customClassId: $customClassId, projectId: $projectId, updatedName: $newName) {
      id
      customClasses {
        id
        name
      }
    }
  }
`;

export const PROJECT_EDIT_CUSTOM_CLASS_COLOR = gql`
  mutation projectEditCustomClassColor($customClassId: ID!, $projectId: ID!, $newColor: InputColor!) {
    projectEditCustomClassColor(customClassId: $customClassId, projectId: $projectId, updatedColor: $newColor) {
      id
      customClasses {
        id
        color {
          r
          g
          b
          a
        }
      }
    }
  }
`;

export const PROJECT_EDIT_LAYER_ELEVATION = gql`
  mutation projectEditLayerElevation($layerIds: [ID!]!, $projectId: ID!, $newElevation: Float!) {
    projectEditLayerElevation(layerIds: $layerIds, projectId: $projectId, newElevation: $newElevation) {
      id
      state {
        orthophotoLayers {
          identifier
          elevation
        }
      }
    }
  }
`;

export const PROJECT_ADD_GUEST = gql`
  mutation projectAddGuest($projectId: ID!, $email: String!, $level: ProjectAccessLevel) {
    projectAddGuest(projectId: $projectId, email: $email, level: $level) {
      id
    }
  }
`;

export const PROJECT_SET_GUEST_ACCESS_LEVEL = gql`
  mutation projectSetGuestAccessLevel($projectId: ID!, $userId: ID!, $level: ProjectAccessLevel!) {
    projectSetGuestAccessLevel(projectId: $projectId, userId: $userId, level: $level) {
      id
    }
  }
`;

export const PROJECT_REMOVE_GUEST = gql`
  mutation projectRemoveGuest($projectId: ID!, $userId: ID!) {
    projectRemoveGuest(projectId: $projectId, userId: $userId) {
      id
    }
  }
`;

export const PROJECT_REQUEST_DOWNLOAD_TOKEN = gql`
  query projectRequestDownloadToken($projectId: ID!) {
    projectRequestDownloadToken(projectId: $projectId)
  }
`;

export const PROJECT_COMMENTS = gql`
  query projectCommentsByObjectId($pagination: CursorPaginationInput, $projectId: ID!, $objectId: ID!) {
    projectCommentsByObjectId(pagination: $pagination, projectId: $projectId, objectId: $objectId) {
      data {
        id
        updatedAt
        createdAt
        comment
        referenceIdentifier
        user {
          id
          name
        }
      }
      nextCursor
      hasMore
    }
  }
`;

export const POINT_CLOUD_RELAUNCH = gql`
  mutation projectPointCloudRelaunch($projectId: ID!, $pointCloudId: ID!) {
    projectPointCloudRelaunch(projectId: $projectId, pointCloudId: $pointCloudId) {
      id
      uploadStatus
      pointClouds {
        id
        hasClassifications
        hasSources
        uploadStatus
        cloudName
        availableClasses
        availableSources
        visible
        displayName
        pointCount
      }
    }
  }
`;
